import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ToolkitEek2ResultsComponent } from '../../components/toolkit/eek2-results/eek2-results.component';
import { ToolkitEek2TherapistResultsComponent } from '../../components/toolkit/eek2-therapist-results/eek2-therapist-results.component';
import { ToolkitSurveyResultsComponent } from '../../components/toolkit/survey-results/survey-results.component';
import { LoaderOverlayComponent } from '../../components/loader-overlay/loader-overlay.component';
import { TranslatePipe } from '../../filters/translate.pipe';
import AngularRoutingHelperService from '../../services/angular-routing-helper.service';
import RoutingService from '../../services/routing.service';
import ToolkitService from '../../services/toolkit.service';
import Toast from '../../services/toast.service';
import { parseJsonLenient } from '../../utils/toolkit-json.util';

@Component({
	standalone: true,
	selector: 'toolkit-assessment-preview',
	templateUrl: './toolkit-assessment-preview.component.html',
	imports: [CommonModule, TranslatePipe, LoaderOverlayComponent, ToolkitEek2ResultsComponent, ToolkitEek2TherapistResultsComponent, ToolkitSurveyResultsComponent]
})
export default class ToolkitAssessmentPreviewComponent implements OnInit {
	private routingService: RoutingService = inject(RoutingService);
	private angularRoutingHelperService: AngularRoutingHelperService = inject(AngularRoutingHelperService);
	private route: ActivatedRoute = inject(ActivatedRoute);
	private toolkitService: ToolkitService = inject(ToolkitService);
	private toast: Toast = inject(Toast);

	vm: any = undefined;

	constructor() {
		this.vm = this;
	}

	ngOnInit() {
		this.routingService.validateAccess(window.location.pathname.indexOf('/my-exercises') === 0 ? 'myExercises' : 'toolkit').then(() => this.onResolve());
	}

	onResolve() {
		this.vm.loading = true;
		this.vm.error = false;

		this.vm.assessmentId = (this.route.snapshot.paramMap.get('id') || '').toString();
		this.vm.isPreview = this.route.snapshot.queryParamMap.get('preview') !== '0';
		this.vm.assignmentId = this.route.snapshot.queryParamMap.get('assignment_id');
		this.vm.clientId = this.route.snapshot.queryParamMap.get('client_id');

		this.vm.assessment = null;
		this.vm.assignment = null;
		this.vm.assignmentNote = null;
		this.vm.assignmentHistory = [];

		this.vm.rawQuestions = null;
		this.vm.questions = null; // parsed questionsJson (code/fullTitle/timeframeText/responseScale/items)
		this.vm.scoring = null; // parsed scoringJson (bands etc) - optional
		this.vm.step = 0;
		this.vm.answers = [];
		this.vm.submitting = false;
		this.vm.isEek2Survey = false;
		this.vm.showEek2Results = false;
		this.vm.showSurveyResults = false;

		this.vm.close = this.close.bind(this);
		this.vm.prev = this.prev.bind(this);
		this.vm.next = this.next.bind(this);
		this.vm.selectAnswer = this.selectAnswer.bind(this);
		this.vm.submit = this.submit.bind(this);

		if (!this.vm.assessmentId) {
			this.vm.loading = false;
			this.vm.error = true;
			return;
		}

		// Optional: load assignment context (therapist preview) to display note and to prefer frozen snapshot JSON.
		if (this.vm.assignmentId && this.vm.clientId) {
			this.toolkitService.getClientDetail({ id: this.vm.clientId }, (detail) => {
				const list: any[] = Array.isArray(detail?.assessmentAssignments) ? detail.assessmentAssignments : [];
				this.vm.assignmentHistory = list;
				const a = list.find((x) => x && x.id?.toString() === this.vm.assignmentId?.toString());
				this.vm.assignment = a || null;
				this.vm.assignmentNote = a?.note || null;
				this.parseAssessmentPayloads(); // in case assessment already loaded
			});
		} else if (this.vm.assignmentId && !this.vm.isPreview) {
			// Client-mode: load assignment from /me endpoint so we can show note + frozen snapshots.
			this.toolkitService.getMyAssessmentAssignment({ id: this.vm.assignmentId }, (a) => {
				this.vm.assignment = a || null;
				this.vm.assignmentNote = a?.note || null;
				this.parseAssessmentPayloads();
			});
		}

		this.toolkitService.getAssessment(
			{ id: this.vm.assessmentId },
			(a) => {
				this.vm.assessment = a || null;
				this.parseAssessmentPayloads();
				this.vm.loading = false;
			},
			() => {
				this.vm.loading = false;
				this.vm.error = true;
			}
		);
	}

	private parseAssessmentPayloads() {
		const a = this.vm.assessment;
		if (!a) return;

		const localizedQuestionsRaw = a.questionsJson || a.questions_json || null;
		const snapshotQuestionsRaw = this.vm.assignment?.assessmentQuestionsSnapshotJson || null;
		const qRaw = localizedQuestionsRaw || snapshotQuestionsRaw || null;
		const sRaw = this.vm.assignment?.assessmentScoringSnapshotJson || a.scoringJson || a.scoring_json || null;

		const questions = parseJsonLenient(qRaw);
		const scoring = parseJsonLenient(sRaw);

		this.vm.rawQuestions = questions && typeof questions === 'object' ? questions : null;
		this.vm.isEek2Survey = this.isEek2Assessment(this.vm.rawQuestions, a);
		this.vm.questions = this.normalizeSurveyQuestions(this.vm.rawQuestions);
		this.vm.scoring = scoring && typeof scoring === 'object' ? scoring : null;

		const items = Array.isArray(this.vm.questions?.items) ? this.vm.questions.items : [];
		this.vm.answers = new Array(items.length).fill(undefined);

		// Client-mode: preload already saved answers (draft or completed) if present on assignment.
		const rawAnswers = this.vm.assignment?.answersJson || this.vm.assignment?.answers_json || null;
		if (rawAnswers) {
			try {
				const parsedAnswers = parseJsonLenient(rawAnswers);
				if (Array.isArray(parsedAnswers)) {
					this.vm.answers = parsedAnswers.slice(0, items.length);
				}
			} catch {}
		}

		// Read-only when completed.
		const status = (this.vm.assignment?.status || '').toString().toLowerCase();
		// Provider preview is always read-only. Client fill-mode is read-only only when already completed.
		this.vm.isReadOnly = !!this.vm.isPreview || (!this.vm.isPreview && (status === 'completed' || !!this.vm.assignment?.completedAt));
		this.vm.showSurveyResults = this.hasCompletedAnswers() && (this.vm.isReadOnly || !!this.vm.isPreview);
		this.vm.showEek2Results = !!this.vm.isPreview && this.vm.isEek2Survey && this.vm.showSurveyResults;
		this.vm.step = 0;
	}

	private isEek2Assessment(questions: any, assessment: any): boolean {
		const instrument = questions?.instrument || null;
		const code = (instrument?.code || questions?.code || assessment?.code || assessment?.title || '').toString().trim().toLowerCase();
		const id = (instrument?.id || '').toString().trim().toLowerCase();
		return id === 'eek2' || code === 'eek-2' || code === 'eek2';
	}

	private normalizeSurveyQuestions(rawQuestions: any): any | null {
		if (!rawQuestions || typeof rawQuestions !== 'object') return null;
		if (Array.isArray(rawQuestions?.items)) return rawQuestions;

		const instrument = rawQuestions?.instrument;
		if (!instrument || !Array.isArray(instrument?.items)) return rawQuestions;

		return {
			...rawQuestions,
			code: instrument?.code || rawQuestions?.code || '',
			title: instrument?.title || rawQuestions?.title || '',
			fullTitle: instrument?.fullTitle || instrument?.title || rawQuestions?.fullTitle || rawQuestions?.title || '',
			description: instrument?.description || instrument?.descriptionEt || '',
			timeframeText: instrument?.timeframeText || instrument?.timeframeTextEt || '',
			itemCount: instrument?.itemCount || instrument.items.length,
			responseScale: Array.isArray(instrument?.responseScale)
				? instrument.responseScale.map((option) => ({
					value: option?.value,
					label: option?.label || option?.labelEt || ''
				}))
				: [],
			items: instrument.items
				.slice()
				.sort((a, b) => (a?.order || 0) - (b?.order || 0))
				.map((item) => ({
					...item,
					text: item?.text || item?.textEt || ''
				}))
		};
	}

	private hasCompletedAnswers(): boolean {
		const items = Array.isArray(this.vm.questions?.items) ? this.vm.questions.items : [];
		if (!items.length || !Array.isArray(this.vm.answers) || this.vm.answers.length < items.length) return false;
		return items.every((_: any, index: number) => this.vm.answers[index] !== undefined && this.vm.answers[index] !== null);
	}

	get totalQuestions(): number {
		const items = Array.isArray(this.vm.questions?.items) ? this.vm.questions.items : [];
		return items.length;
	}

	get currentItem(): any | null {
		const items = Array.isArray(this.vm.questions?.items) ? this.vm.questions.items : [];
		return items[this.vm.step] || null;
	}

	get progressPercent(): number {
		const total = this.totalQuestions;
		if (!total) return 0;
		return ((this.vm.step + 1) / total) * 100;
	}

	selectAnswer(value: any) {
		if (this.vm.isReadOnly) return;
		if (this.vm.submitting) return;
		this.vm.answers[this.vm.step] = value;
	}

	prev() {
		if (this.vm.step > 0) {
			this.vm.step--;
			window.scrollTo(0, 0);
		}
	}

	next() {
		if (this.vm.step < this.totalQuestions - 1) {
			this.vm.step++;
			window.scrollTo(0, 0);
		}
	}

	submit() {
		if (this.vm.isPreview) {
			this.close();
			return;
		}
		if (this.vm.isReadOnly) {
			this.close();
			return;
		}

		if (!this.vm.assignmentId) {
			this.toast.showWithTranslation('Toolkit.Assessment.Preview.NoAssignment');
			return;
		}

		const unanswered = (this.vm.answers || []).filter((x) => x === undefined || x === null).length;
		if (unanswered > 0) {
			this.toast.showWithTranslation('Toolkit.Assessment.Preview.Incomplete');
			return;
		}

		this.vm.submitting = true;
		this.toolkitService.submitMyAssessmentAnswers(
			{ assignmentId: this.vm.assignmentId },
			{ answers: this.vm.answers },
			() => {
				this.vm.submitting = false;
				this.toast.showWithTranslation('Toolkit.Assessment.Preview.Submitted');
				this.vm.isReadOnly = true;
				this.vm.showSurveyResults = true;
				window.scrollTo(0, 0);
			},
			() => {
				this.vm.submitting = false;
				this.toast.showWithTranslation('Toolkit.Assessment.Preview.SubmitError');
			}
		);
	}

	close() {
		const returnTo = (this.route.snapshot.queryParamMap.get('return_to') || '').toString();
		this.angularRoutingHelperService.setPath(returnTo || '/toolkit', false, false);
	}
}
